import React, { useState, useEffect, useMemo, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Outlet, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { jwtDecode } from 'jwt-decode';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import {
  getNewAccessAndRefreshToken,
  logoutUser,
  setGlobalAccessToken,
  getStoredAccessToken,
  setStoredAccessToken,
  setStoredRefreshToken,
} from './API/Api';

import TopBar from './components/shared/TopBar';
import SideBar from './components/shared/SideBar';
import { RightContainer } from './components/shared/utils';

import Login from './components/login/Login';
import ResetPassword from './components/login/ResetPassword';
import PageNotFound from './PageNotFound';
import AdminTable from './components/admin/AdminTable';
import Profile from './components/shared/profile/Profile';
import ChangeLog from './components/shared/profile/ChangeLog';
import ApiCount from './components/shared/profile/ApiCount';

import SuperAdminCompaniesTable from './components/super_admin/SuperAdminCompaniesTable';
import SuperAdminAllKycBilling from './components/super_admin/SuperAdminAllKycBilling';

import _KycAmlDashboard from './components/kyc_aml_portal/dashboard/_KycAmlDashboard';
import _KycAmlTranscationHistory from './components/kyc_aml_portal/transaction_history/_KycAmlTranscationHistory';
import _KycAmlCustomerListTable from './components/kyc_aml_portal/customer_list/_KycAmlCustomerListTable';
import _KycAmlArchivedCustomerTable from './components/kyc_aml_portal/customer_list/_KycAmlArchivedCustomerTable';
import __KycAmlForm from './components/kyc_aml_portal/form/__KycAmlForm';

import _FnPDashboard from './components/fit_and_proper_declaration/dashboard/_FnPDashboard';
import _FnPReportingTable from './components/fit_and_proper_declaration/reporting_table/_FnPReportingTable';
import _FnPArchivedReportingTable from './components/fit_and_proper_declaration/reporting_table/_FnPArchivedReportingTable';
import _FnPForm from './components/fit_and_proper_declaration/form/_FnPForm';

import _ComplianceDashboard from './components/compliance_monitoring_program/dashboard/_ComplianceDashboard';
import __ComplianceTable from './components/compliance_monitoring_program/__ComplianceTable';
import _ComplianceFormArchivedTable from './components/compliance_monitoring_program/form_user_table/_ComplianceFormArchivedTable';
import _CreateUpdateComplianceTopicForm from './components/compliance_monitoring_program/topics_monitoring_table/_CreateUpdateComplianceTopicForm';
import _ComplianceFormResponse from './components/compliance_monitoring_program/form_user_table/_ComplianceFormResponse';
import AgreementModal from './components/shared/AgreementModal';
import AbacusAsiaWealthAgreementModal from './components/shared/AbacusAsiaWealthAgreementModal';
import SubscriptionExpiredModal from './components/shared/SubscriptionExpiredModal';
import ShortLinkRedirect from './components/ShortLinkRedirect';

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [encodedAccessToken, setEncodedAccessToken] = useState(); // this stores access token details in memory // context is declared at bottom of App.js file
  const [open, setOpen] = useState(true);
  const [hideContent, setHideContent] = useState(true);

  const Container = useMemo(() => {
    const ContainerComponent = open
      ? RightContainer
      : React.memo(({ children, ...props }) => (
          <div {...props} className=' ml-20 flex-grow  px-9 pt-4 max-w-screen-2xl h-screen'>
            {/* // className="flex-1 ml-20 flex-grow  px-9 pt-4 overflow-y-auto overflow-x-auto max-w-screen-2xl h-screen" why does it trigger another scrollbar, and why is itboth has to be present */}
            {children}
          </div>
        ));

    return React.memo(({ children }) => <ContainerComponent>{children}</ContainerComponent>);
  }, [open]);

  useEffect(() => {
    const refreshAccessToken = async () => {
      try {
        const currentUrl = window.location.pathname;

        // Skip refresh token checks on external form pages and login pages
        if (['/login', '/form/kyc-aml', '/reset-password'].includes(currentUrl) || currentUrl.startsWith('/s/')) {
          console.log('Skipping token refresh on external page');
          setHideContent(false);
          return;
        }

        // Restore session from localStorage when state is empty (e.g. after reload or new tab)
        if (!encodedAccessToken && !isLoggedIn) {
          const storedAccess = getStoredAccessToken();
          if (storedAccess) {
            setEncodedAccessToken(storedAccess);
            setGlobalAccessToken(storedAccess);
            setIsLoggedIn(true);
            setHideContent(false);
            return;
          }
        }

        // If we have a valid accessToken and are logged in, check if we need to refresh
        // Only refresh proactively if token is close to expiring (within 2 minutes)
        if (encodedAccessToken && isLoggedIn) {
          try {
            const decodedToken = jwtDecode(encodedAccessToken);
            const currentTime = Date.now() / 1000;
            const timeUntilExpiry = decodedToken.exp - currentTime;

            // If token is still valid for more than 2 minutes, skip refresh
            if (timeUntilExpiry > 120) {
              setHideContent(false);
              return;
            }
          } catch (decodeError) {
            console.warn('Failed to decode token, attempting refresh:', decodeError);
            // If we can't decode the token, try to refresh it
          }
        }

        console.log('Refreshing access token...');
        const response = await getNewAccessAndRefreshToken();

        if (response?.data?.accessToken) {
          const newAccessToken = response.data.accessToken;
          const newRefreshToken = response.data.refreshToken;
          setEncodedAccessToken(newAccessToken);
          setGlobalAccessToken(newAccessToken);
          setStoredAccessToken(newAccessToken);
          if (newRefreshToken) setStoredRefreshToken(newRefreshToken);
          setIsLoggedIn(true);
          setHideContent(false);
          console.log('Token refreshed successfully');
        } else {
          // Invalid refresh response
          console.warn('Invalid refresh token response:', response);

          // If we have a valid accessToken, continue using it
          // The API interceptor will handle token refresh on next API call
          if (encodedAccessToken && isLoggedIn) {
            setHideContent(false);
            return;
          }

          // No valid token at all, redirect to login
          console.log('No valid tokens, redirecting to login');
          logoutUser().then(() => {
            window.location.href = '/login';
          });
          setEncodedAccessToken('');
          setGlobalAccessToken('');
          setIsLoggedIn(false);
        }
      } catch (error) {
        console.error('Error refreshing access token:', error);

        // If we have a valid accessToken, continue using it
        // The API interceptor will handle token refresh on next API call
        if (encodedAccessToken && isLoggedIn) {
          console.log('Continuing with existing token despite refresh error');
          setHideContent(false);
          return;
        }

        // No valid token, redirect to login
        console.log('No valid tokens after refresh error, redirecting to login');
        logoutUser().then(() => {
          window.location.href = '/login';
        });
        setHideContent(false);
        setGlobalAccessToken('');
        setIsLoggedIn(false);
      }
    };

    // Initial token refresh check
    refreshAccessToken();

    // Set up interval to refresh token every 14.4 minutes (864 seconds)
    // This is slightly less than typical 15-minute token expiry to refresh proactively
    const intervalId = setInterval(refreshAccessToken, 880000); // 14.40 minutes = 880000 milliseconds

    return () => clearInterval(intervalId); // Clean up the interval on component unmount
  }, [isLoggedIn, encodedAccessToken]);

  const blankPage = <div className='min-h-screen w-full bg-white fixed inset-0 z-50'></div>;
  if (hideContent) {
    return blankPage; // show a blank white screen during intial load.
  }

  return (
    <TokenContext.Provider value={[encodedAccessToken, setEncodedAccessToken]}>
      <BrowserRouter>
        {isLoggedIn ? (
          <>
            <TopBar setIsLoggedIn={setIsLoggedIn} />
            <SideBar setOpen={setOpen} open={open} />
            <ToastContainer pauseOnHover={false} pauseOnFocusLoss={false} limit={3} autoClose={2500} />
            <AgreementModal />
            <AbacusAsiaWealthAgreementModal />
            <SubscriptionExpiredModal />

            <Container>
              <Routes>
                <Route path='/' element={<Home />} />
                <Route path='/home' element={<Home />} />
                <Route path='/reset-password' element={<ResetPassword />} />
                <Route path='/profile' element={<Profile />} />
                <Route path='/change-log' element={<ChangeLog />} />
                <Route path='/api-count' element={<ApiCount />} />

                <Route element={<AdminProtectedRoutes />}>
                  <Route path='/admin' element={<AdminTable />} />
                  <Route path='/super-admin' element={<SuperAdminCompaniesTable />} />
                </Route>

                <Route element={<KycProtectedRoutes />}>
                  <Route path='/kyc-aml' element={<_KycAmlDashboard />} />
                  <Route path='/kyc-aml/transaction-history' element={<_KycAmlTranscationHistory />} />
                  <Route path='/kyc-aml/customer-list' element={<_KycAmlCustomerListTable />} />
                  <Route path='/kyc-aml/archived-customer-list' element={<_KycAmlArchivedCustomerTable />} />
                  <Route path='/kyc-aml/form' element={<__KycAmlForm />} />
                  <Route path='/form/kyc-aml' element={<__KycAmlForm />} />{' '}
                  {/*  To access this regardless log in or not */}
                </Route>

                <Route element={<CmpProtectedRoutes />}>
                  <Route path='/compliance-monitoring' element={<_ComplianceDashboard />} />
                  <Route path='/compliance-monitoring/monitoring' element={<__ComplianceTable />} />
                  <Route path='/compliance-monitoring/archived-monitoring' element={<_ComplianceFormArchivedTable />} />
                  <Route path='/compliance-monitoring/topic-form' element={<_CreateUpdateComplianceTopicForm />} />
                  <Route path='/compliance-monitoring/user-form' element={<_ComplianceFormResponse />} />
                </Route>

                <Route element={<SuperProtectedRoutes />}>
                  <Route path='/super' element={<SuperAdminCompaniesTable />} />
                  <Route path='/super/kyc' element={<SuperAdminAllKycBilling />} />
                </Route>

                <Route path='/fit-and-proper' element={<_FnPDashboard />} />
                <Route path='/fit-and-proper/reporting-status' element={<_FnPReportingTable />} />
                <Route path='/fit-and-proper/archived-reporting-status' element={<_FnPArchivedReportingTable />} />
                <Route path='/fit-and-proper/form' element={<_FnPForm />} />

                <Route path='/s/:shortCode' element={<ShortLinkRedirect />} />
                <Route path='*' element={<PageNotFound />} />
              </Routes>
            </Container>
          </>
        ) : (
          <Routes>
            <Route path='/form/kyc-aml' element={<__KycAmlForm />} />
            <Route path='/' element={blankPage} />
            <Route path='/login' element={<Login setIsLoggedIn={setIsLoggedIn} />} />
            <Route path='/reset-password' element={<ResetPassword />} />
            <Route path='/s/:shortCode' element={<ShortLinkRedirect />} />
            <Route path='*' element={<PageNotFound />} />
          </Routes>
        )}
      </BrowserRouter>
    </TokenContext.Provider>
  );
}

export default App;

export const TokenContext = createContext();

const CmpProtectedRoutes = () => {
  const [encodedAccessToken] = useContext(TokenContext);
  const decodedToken = jwtDecode(encodedAccessToken);
  return decodedToken.subscriptions.cmp ? <Outlet /> : <Navigate to='/' />;
};

const KycProtectedRoutes = () => {
  const [encodedAccessToken] = useContext(TokenContext);
  const decodedToken = jwtDecode(encodedAccessToken);
  return decodedToken.subscriptions.kyc_aml ? <Outlet /> : <Navigate to='/' />;
};

// const FnpProtectedRoutes = () => {
// const [encodedAccessToken] = useContext(TokenContext)
//   return decodedToken.subscriptions.kyc_aml ? <Outlet/> : <Navigate to="/profile" />
// }

const AdminProtectedRoutes = () => {
  const [encodedAccessToken] = useContext(TokenContext);
  const decodedToken = jwtDecode(encodedAccessToken);
  return decodedToken.role == 'admin' ? <Outlet /> : <Navigate to='/' />;
};

const SuperProtectedRoutes = () => {
  const [encodedAccessToken] = useContext(TokenContext);
  const decodedToken = jwtDecode(encodedAccessToken);
  return decodedToken.role == 'superAdmin' ? <Outlet /> : <Navigate to='/' />;
};

const Home = () => {
  return <div> ← Navigate app with SideBar </div>;
};
